import React, { useEffect, useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  FormControlLabel,
  Grid,
  Box,
  Typography,
  FormHelperText,
  Button,
  Stack,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import FormTemplate from "./FormTemplate";
import { SingleInputTimeRangeField } from "@mui/x-date-pickers-pro/SingleInputTimeRangeField";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ConditionBuilder from "../components/ConditionBuilder";
import MultiSelectWithSelectAll from "../components/MultiSelectWithAll";
import SelectArrow from "../styles/icons/SelectArrow.jsx";
import { queryTypesFlat } from "../models/nodeTypesConfig.js";
import PipeBuilderMainMenu from "../components/flow/menus/PipeBuilderMainMenu";
import LeadAgeRestrictionPanel from "../components/flow/menus/LeadAgeRestrictionPanel";
import { normalizeLeadAgeRestriction } from "../utils/workflow/leadAgeRestriction";
import FieldLabelWithHelp from "../components/common/FieldLabelWithHelp";
import FieldHelpTooltip from "../components/common/FieldHelpTooltip";
import { sectionTooltips } from "../utils/workflow/journeyTooltips";

const NodesDynamicForm = ({
  formFields,
  title,
  nodeTooltip,
  onSubmit,
  onClose,
  initialData = {},
  onSaveTemplate,
}) => {
  console.log(initialData);
  const [valid, setValid] = useState(false);
  const [touched, setTouched] = useState([]);
  const [conditionGlobalMode, setConditionGlobalMode] = useState("whitelist");
  const [saveConfigOpen, setSaveConfigOpen] = useState(false);
  const [saveConfig, setSaveConfig] = useState({
    enabled: Boolean(initialData?.saveForReuse || initialData?.savedQueryId),
    name: initialData?.queryName || initialData?.savedQueryName || "",
    description:
      initialData?.queryDescription || initialData?.savedQueryDescription || "",
    savedId: initialData?.savedQueryId || null,
  });
  const [saveConfigError, setSaveConfigError] = useState("");
  const [saveConfigSuccess, setSaveConfigSuccess] = useState("");
  const COLOR_HEADER = "#1E1E1E";
  const COLOR_ACCENT = "#0DA3E2";
  const COLOR_SURFACE = "#2C2B2D";
  const hasConditionBuilder = formFields.some(
    (field) => field.type === "conditionBuilder"
  );
  const hasCustomQuerySaveConfig = formFields.some(
    (field) => field.id === "saveForReuse" || field.id === "queryName"
  );
  const defaultSelectMenuProps = {
    PaperProps: {
      sx: {
        mt: 0.5,
        maxHeight: 300,
        overflowY: "auto",
        bgcolor: COLOR_HEADER,
        backgroundImage: "none",
        color: "#FFFFFF",
        border: `1px solid ${COLOR_ACCENT}`,
        borderRadius: "3px",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: COLOR_HEADER,
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#FFFFFF",
          borderRadius: "3px",
        },
        "& .MuiMenuItem-root": {
          minHeight: 42,
          color: "#FFFFFF",
          fontSize: "14px",
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: "rgba(13,163,226,0.22)",
          },
          "&:hover": {
            backgroundColor: "rgba(13,163,226,0.12)",
          },
        },
      },
    },
  };
  const [formData, setFormData] = useState(() => {
    const initialState = formFields.reduce((acc, field) => {
      if (field.type === "checkboxGroup") {
        acc[field.id] = [];
      } else if (field.type === "checkbox") {
        acc[field.id] = field.defaultValue || false;
      } else if (field.type === "conditionBuilder") {
        acc[field.id] = [];
      } else if (field.type === "timeRange") {
        acc[field.id] = null;
      } else if (field.type === "dateTimePicker") {
        acc[field.id] = null;
      } else if (field.type === "leadAgeRestriction") {
        acc[field.id] = field.defaultValue || { enabled: false, minDays: 14 };
      } else {
        acc[field.id] = "";
      }
      return acc;
    }, {});

    return {
      ...initialState,
      ...Object.fromEntries(
        Object.entries(initialData).map(([key, value]) => {
          const field = formFields.find((f) => f.id === key);
          if (field && field.type === "timeRange") {
            if (Array.isArray(value) && value.length === 2) {
              const [start, end] = value;
              if (start && end) {
                const startTime = dayjs(start, "HH:mm");
                const endTime = dayjs(end, "HH:mm");

                if (
                  startTime.isValid() &&
                  endTime.isValid() &&
                  endTime.isAfter(startTime)
                ) {
                  return [key, value];
                }
              }
            }
            return [key, null];
          }

          if (Array.isArray(value)) {
            if (value.length > 0 && value[0].hasOwnProperty("value")) {
              return [key, value];
            }
            if (field && field.type === "checkboxGroup") {
              return [key, value];
            }
          }
          if (field && field.type === "leadAgeRestriction") {
            return [key, normalizeLeadAgeRestriction(value)];
          }
          return [key, value ?? initialState[key]];
        })
      ),
    };
  });

  const handleTouched = (field) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const handleChange = (field) => (event) => {
    if (field.type === "checkboxGroup") {
      const { value, checked } = event.target;
      setFormData((prevData) => {
        const currentValues = Array.isArray(prevData[field.id])
          ? prevData[field.id]
          : [];

        const updatedValues = checked
          ? [...currentValues, value]
          : currentValues.filter((v) => v !== value);
        return { ...prevData, [field.id]: updatedValues };
      });
    } else if (field.type === "checkbox") {
      setFormData({
        ...formData,
        [field.id]: event.target.checked,
      });
    } else {
      setFormData({
        ...formData,
        [field.id]: event.target.value,
      });
    }
  };

  const handleSubmit = () => {
    const validationErrors = collectValidationErrors();
    if (validationErrors.length > 0) {
      const touchedAllVisible = formFields
        .filter((field) => isFieldVisible(field))
        .reduce((acc, field) => {
          acc[field.id] = true;
          return acc;
        }, {});
      setTouched((prev) => ({ ...prev, ...touchedAllVisible }));
      return;
    }

    console.log(formData);

    const updatedFormData = { ...formData };
    if (hasCustomQuerySaveConfig) {
      updatedFormData.saveForReuse = saveConfig.enabled;
      updatedFormData.queryName = saveConfig.name?.trim() || "";
      updatedFormData.queryDescription = saveConfig.description?.trim() || "";
      if (saveConfig.savedId) {
        updatedFormData.savedQueryId = saveConfig.savedId;
      }
      if (saveConfig.enabled && !updatedFormData.queryName) {
        setSaveConfigOpen(true);
        setSaveConfigError("Query Name is required when saving for reuse.");
        return;
      }
    }

    // Helper function: Convert a raw item (primitive or object)
    // into { value, label } only if needed.
    function coerceToValueLabel(field, item) {
      // If it's already { value, label }, keep it as is.
      if (typeof item === "object" && item.value !== undefined) {
        return item;
      }

      // Otherwise, find the matching option to get a label.
      const option = field.options.find((opt) => opt.value === item);
      return {
        value: item,
        label: option ? option.label : item,
      };
    }

    formFields.forEach((field) => {
      // Only run this logic for selects and checkboxGroups
      if (field.type === "select" || field.type === "checkboxGroup") {
        const currentValue = updatedFormData[field.id];

        // Multi-select or checkboxGroup => array
        if (Array.isArray(currentValue)) {
          updatedFormData[field.id] = currentValue.map((item) =>
            coerceToValueLabel(field, item)
          );
        }
        // Single-select => a single value
        else if (currentValue) {
          updatedFormData[field.id] = coerceToValueLabel(field, currentValue);
        }
      }
    });

    // Pass the updated form data to the parent
    onSubmit(updatedFormData);
  };

  /* ---- conditional field helpers ---- */
  const isFieldDisabled = (field) => {
    if (!field.disabledWhen) return false;
    const dep = formData[field.disabledWhen.field];
    const raw = dep?.value ?? dep; // handle both {value,label} and plain string
    const expectedValue = field.disabledWhen.value;
    if (Array.isArray(expectedValue)) {
      return expectedValue.includes(raw);
    }
    return raw === expectedValue;
  };

  const isFieldVisible = (field) => {
    if (!field.visibleWhen) return true;
    const dep = formData[field.visibleWhen.field];
    const raw = dep?.value ?? dep;
    const expectedValue = field.visibleWhen.value;
    if (Array.isArray(expectedValue)) {
      return expectedValue.includes(raw);
    }
    return raw === expectedValue;
  };

  const isFormValid = () => {
    const hasScheduleRangeFields =
      formFields.some((f) => f.id === "startDateTime") &&
      formFields.some((f) => f.id === "endDateTime");
    if (hasScheduleRangeFields) {
      const start = formData.startDateTime ? dayjs(formData.startDateTime) : null;
      const end = formData.endDateTime ? dayjs(formData.endDateTime) : null;
      if (start && end && start.isValid() && end.isValid() && !end.isAfter(start)) {
        return false;
      }
    }

    return formFields.every((field) => {
      // hidden fields are always valid
      if (!isFieldVisible(field)) return true;
      // disabled fields are always valid
      if (isFieldDisabled(field)) return true;

      const value = formData[field.id];

      if (field.type === "conditionBuilder") {
        return (
          Array.isArray(value) && // must be an array
          value.length > 0 && // at least one row
          value.every(
            (row) =>
              row.type?.toString().trim() && // non-empty type
              row.value?.toString().trim() // non-empty value
          )
        );
      }

      // --- Dedicated check for the Pipe Builder ---
      if (field.type === "pipeBuilder") {
        const isNumericString = (s) =>
          typeof s === "string" && s.trim() !== "" && !Number.isNaN(Number(s));

        const looksLikeDate = (s) => typeof s === "string" && /[-/T:]/.test(s);

        return (
          Array.isArray(value) &&
          value.length > 0 &&
          value.every((row) => {
            const baseOk =
              row.table?.toString().trim() &&
              row.field?.toString().trim() &&
              row.operator?.toString().trim();
            if (!baseOk) return false;

            const op = String(row.operator || "").toLowerCase();

            if (op.includes("between")) {
              if (typeof row.value !== "string") return false;
              const [a = "", b = ""] = row.value.split("|");
              if (!a || !b) return false;

              if (isNumericString(a) && isNumericString(b)) {
                const min = Number(a);
                const max = Number(b);
                return max >= min;
              }

              if (looksLikeDate(a) && looksLikeDate(b)) {
                const s = dayjs(a);
                const e = dayjs(b);
                return (
                  s.isValid() && e.isValid() && (e.isAfter(s) || e.isSame(s))
                );
              }

              return false;
            }

            return String(row.value ?? "").trim().length > 0;
          })
        );
      }

      if (field.type === "timeRange") {
        if (!field.required && (!value || !Array.isArray(value))) {
          return true;
        }

        if (!field.required && Array.isArray(value)) {
          const [start, end] = value;

          if (!start && !end) {
            return true;
          }

          if (!start || !end) {
            return false;
          }

          const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
          if (!timeRegex.test(start) || !timeRegex.test(end)) {
            return false;
          }

          const startTime = dayjs(start, "HH:mm");
          const endTime = dayjs(end, "HH:mm");

          if (!startTime.isValid() || !endTime.isValid()) {
            return false;
          }

          if (!endTime.isAfter(startTime)) {
            return false;
          }

          return true;
        }

        if (field.required) {
          if (!Array.isArray(value) || value.length !== 2) return false;

          const [start, end] = value;
          if (!start || !end) return false;

          const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
          if (!timeRegex.test(start) || !timeRegex.test(end)) return false;

          const startTime = dayjs(start, "HH:mm");
          const endTime = dayjs(end, "HH:mm");

          if (!startTime.isValid() || !endTime.isValid()) return false;

          return endTime.isAfter(startTime);
        }

        return true;
      }

      // --- Existing rules for the other field types ---
      if (field.type === "checkboxGroup") {
        return !field.required
          ? true
          : Array.isArray(value) && value.length > 0;
      }

      if (field.type === "dateTimePicker") {
        if (!field.required) return true;
        const d = dayjs(value);
        return d.isValid() && d.isAfter(dayjs());
      }

      if (field.type === "timePicker") {
        if (!field.required) return true;
        if (!value) return false;
        return dayjs(value, "HH:mm").isValid();
      }

      return field.required ? Boolean(value) : true;
    });
  };

  const collectValidationErrors = () => {
    const errors = [];
    const start = formData.startDateTime ? dayjs(formData.startDateTime) : null;
    const end = formData.endDateTime ? dayjs(formData.endDateTime) : null;

    if (start && end && start.isValid() && end.isValid() && !end.isAfter(start)) {
      errors.push("End date & time must be after start date & time.");
    }

    formFields.forEach((field) => {
      if (!isFieldVisible(field) || isFieldDisabled(field)) return;
      const value = formData[field.id];

      if (field.type === "pipeBuilder") {
        const hasRows = Array.isArray(value) && value.length > 0;
        if (!hasRows) {
          errors.push("Please add at least one query condition.");
        }
        return;
      }

      if (field.type === "timeRange") {
        const message = getTimeRangeError(field.id);
        if (message) {
          errors.push(`${field.label}: ${message}`);
        }
        return;
      }

      if (field.type === "timePicker") {
        if (!value) {
          errors.push(`${field.label} is required.`);
          return;
        }
        if (!dayjs(value, "HH:mm").isValid()) {
          errors.push(`${field.label} is invalid.`);
        }
        return;
      }

      if (field.type === "checkboxGroup") {
        if (field.required && (!Array.isArray(value) || value.length === 0)) {
          errors.push(`${field.label} is required.`);
        }
        return;
      }

      if (field.type === "dateTimePicker") {
        if (field.required) {
          if (!value) {
            errors.push(`${field.label} is required.`);
            return;
          }
          const d = dayjs(value);
          if (!d.isValid()) {
            errors.push(`${field.label} is invalid.`);
            return;
          }
          if (!d.isAfter(dayjs())) {
            errors.push(`${field.label} must be in the future.`);
          }
        }
        return;
      }

      if (field.required && !value) {
        errors.push(`${field.label} is required.`);
      }
    });

    return errors;
  };

  const getTimeRangeError = (fieldId) => {
    const field = formFields.find(
      (f) => f.id === fieldId && f.type === "timeRange"
    );
    if (!field) return null;

    const value = formData[fieldId];

    if ((!value || !Array.isArray(value)) && !field.required) {
      return null;
    }

    if ((!value || !Array.isArray(value)) && field.required) {
      return "Please select a valid time range";
    }

    if (Array.isArray(value) && value.length !== 2) {
      return "Please select a valid time range";
    }

    const [start, end] = value;

    if (!start && !end && !field.required) {
      return null;
    }

    if (!start && !end && field.required) {
      return "Please select both start and end times";
    }

    if (!start || !end) {
      return "Please select both start and end times";
    }

    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(start) || !timeRegex.test(end)) {
      return "Invalid time format";
    }

    const startTime = dayjs(start, "HH:mm");
    const endTime = dayjs(end, "HH:mm");

    if (!startTime.isValid() || !endTime.isValid()) {
      return "Invalid time format";
    }

    if (!endTime.isAfter(startTime)) {
      return "End time must be after start time";
    }

    return null;
  };

  useEffect(() => {
    setValid(isFormValid());
  }, [formData]);

  return (
    <>
      <FormTemplate
        title={
          nodeTooltip ? (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              {title}
              <FieldHelpTooltip title={nodeTooltip} placement="bottom" />
            </Box>
          ) : (
            title
          )
        }
        handleSubmit={handleSubmit}
        isValid={valid}
        disableSubmitWhenInvalid={false}
        onClose={onClose}
        extraLeftAction={
          hasCustomQuerySaveConfig ? (
            <Button
              variant="outlined"
              onClick={() => {
                setSaveConfigError("");
                setSaveConfigSuccess("");
                setSaveConfigOpen(true);
              }}
              disabled={!valid}
              sx={{
                width: "187px",
                minWidth: "187px",
                height: "50px",
                ml: "16px",
                borderColor: COLOR_ACCENT,
                color: "#FFFFFF",
                textTransform: "none",
                borderRadius: "7px",
                "&:hover": {
                  borderColor: COLOR_ACCENT,
                  bgcolor: "rgba(13, 163, 226, 0.08)",
                },
                "&.Mui-disabled": {
                  borderColor: "rgba(13, 163, 226, 0.45)",
                  color: "#FFFFFF",
                  opacity: 0.7,
                },
              }}
            >
              <Typography color="#FFFFFF" fontWeight={700} fontSize="20px" lineHeight={1}>
                Save Query
              </Typography>
            </Button>
          ) : null
        }
        headerRightAction={
          hasConditionBuilder ? (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
              <FieldHelpTooltip
                title={sectionTooltips.includeExcludeToggle}
                placement="bottom"
              />
              <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                height: 36,
                border: `1px solid ${COLOR_ACCENT}`,
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <Button
                onClick={() => setConditionGlobalMode("whitelist")}
                sx={{
                  minWidth: "121px",
                  height: "36px",
                  px: "16px",
                  py: "6px",
                  borderRadius: 0,
                  borderRight: `1px solid ${COLOR_ACCENT}`,
                  textTransform: "uppercase",
                  fontFamily: "Assistant, sans-serif",
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: 1,
                  letterSpacing: 0,
                  color: "#FFFFFF",
                  backgroundColor:
                    conditionGlobalMode === "whitelist"
                      ? COLOR_ACCENT
                      : COLOR_HEADER,
                  "&:hover": {
                    backgroundColor:
                      conditionGlobalMode === "whitelist"
                        ? COLOR_ACCENT
                        : COLOR_HEADER,
                    opacity: 0.92,
                  },
                }}
              >
                Include All
              </Button>
              <Button
                onClick={() => setConditionGlobalMode("blacklist")}
                sx={{
                  minWidth: "114px",
                  height: "36px",
                  px: "16px",
                  py: "6px",
                  borderRadius: 0,
                  textTransform: "uppercase",
                  fontFamily: "Assistant, sans-serif",
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: 1,
                  letterSpacing: 0,
                  color: "#FFFFFF",
                  backgroundColor:
                    conditionGlobalMode === "blacklist"
                      ? COLOR_ACCENT
                      : COLOR_HEADER,
                  "&:hover": {
                    backgroundColor:
                      conditionGlobalMode === "blacklist"
                        ? COLOR_ACCENT
                        : COLOR_HEADER,
                    opacity: 0.92,
                  },
                }}
              >
                Exclude All
              </Button>
            </Box>
            </Box>
          ) : null
        }
      >
      <Grid
        container
        spacing={2}
        sx={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "flex-start",
          width: "100%",
          padding: "16px",
          color: "#FFFFFF",
          bgcolor: "#2C2B2D",
        }}
      >
        {formFields
          .filter(
            (field) =>
              !(
                hasCustomQuerySaveConfig &&
                ["saveForReuse", "queryName", "queryDescription"].includes(
                  field.id
                )
              )
          )
          .map((field) => {
          if (!isFieldVisible(field)) return null;
          const fieldDisabled = isFieldDisabled(field);
          const isSaveQueryMetaField = field.id === "queryName";
          const isSaveQueryToggle = field.id === "saveForReuse";

          switch (field.type) {
            case "text":
            case "email":
            case "number":
              return (
                <Grid
                  item
                  direction="column"
                  xs={12}
                  key={field.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    gap: 1,
                    opacity: fieldDisabled ? 0.45 : 1,
                    pointerEvents: fieldDisabled ? "none" : "auto",
                  }}
                >
                  <FieldLabelWithHelp
                    label={field.label}
                    tooltip={field.tooltip}
                    color={isSaveQueryMetaField ? "rgba(255,255,255,0.85)" : "#0DA3E2"}
                  />
                  <TextField
                    type={field.type}
                    value={formData[field.id]}
                    onChange={handleChange(field)}
                    required={field.required}
                    disabled={fieldDisabled}
                    fullWidth
                    sx={{
                      borderRadius: "7px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "7px",
                        color: "#FFFFFF",
                        bgcolor: COLOR_HEADER,
                        "& fieldset": {
                          borderColor: isSaveQueryMetaField
                            ? "rgba(173, 180, 189, 0.45)"
                            : "#0DA3E2",
                        },
                        "&:hover fieldset": {
                          borderColor: isSaveQueryMetaField
                            ? "rgba(173, 180, 189, 0.65)"
                            : "#0DA3E2",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: isSaveQueryMetaField
                            ? "rgba(173, 180, 189, 0.85)"
                            : "#0DA3E2",
                        },
                      },
                    }}
                    InputProps={{
                      sx: {
                        height: 56,
                        fontSize: "0.95rem",
                        px: "14px",
                      },
                    }}
                    onBlur={() => handleTouched(field.id)}
                    error={
                      field.required && !formData[field.id] && touched[field.id]
                    }
                    helperText={
                      field.required && touched[field.id] && !formData[field.id]
                        ? "Field is required"
                        : ""
                    }
                  />
                </Grid>
              );
            case "select":
              return (
                <Grid
                  item
                  xs={12}
                  key={field.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    opacity: fieldDisabled ? 0.45 : 1,
                    pointerEvents: fieldDisabled ? "none" : "auto",
                  }}
                >
                  <FormControl
                    fullWidth
                    onBlur={() => handleTouched(field.id)}
                    error={
                      field.required && !formData[field.id] && touched[field.id]
                    }
                    required={field.required}
                  >
                    <FieldLabelWithHelp label={field.label} tooltip={field.tooltip} />
                    <Select
                      value={
                        formData[field.id]?.value || formData[field.id] || ""
                      }
                      onChange={handleChange(field)}
                      MenuProps={defaultSelectMenuProps}
                      sx={{
                        borderRadius: "7px",
                        minHeight: 56,
                        height: 56,
                        color: "#FFFFFF",
                        bgcolor: COLOR_HEADER,
                        "& fieldset": {
                          borderColor: "#0DA3E2",
                        },
                        "&:hover fieldset": {
                          borderColor: "#0DA3E2",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#0DA3E2",
                        },
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          px: "14px",
                          py: 0,
                        },
                      }}
                      IconComponent={SelectArrow}
                    >
                      <MenuItem value="">
                        <em style={{ color: "rgba(255,255,255,0.55)" }}>None</em>
                      </MenuItem>
                      {field.options
                        ?.filter(
                          (option) =>
                            option &&
                            option.value != null &&
                            option.label != null
                        )
                        .map((option) => (
                          <MenuItem
                            key={`${field.id}-${option.value}`}
                            value={option.value}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                      {field.required &&
                      touched[field.id] &&
                      !formData[field.id]
                        ? "Field is required"
                        : ""}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              );
            case "checkbox":
              return (
                <Grid
                  item
                  xs={12}
                  key={field.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "left",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formData[field.id])}
                        onChange={handleChange(field)}
                        sx={{
                          color: isSaveQueryToggle
                            ? "rgba(173, 180, 189, 0.8)"
                            : undefined,
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                        {field.label}
                        <FieldHelpTooltip title={field.tooltip} />
                      </Box>
                    }
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        color: isSaveQueryToggle
                          ? "rgba(255,255,255,0.9)"
                          : "inherit",
                        fontSize: isSaveQueryToggle ? "0.95rem" : "1rem",
                      },
                    }}
                  />
                  {field.id === "allowDuplicateSends" ? (
                    <FormHelperText sx={{ color: "rgba(255,255,255,0.65)", ml: 0 }}>
                      כשכבוי — ליד שעובד פעם אחת לא ייכלל ב-batches עתידיים. שליחות כפולות באותו יום חסומות תמיד ברמת הג'ירני.
                    </FormHelperText>
                  ) : null}
                </Grid>
              );

            case "checkboxGroup":
              return (
                <Grid
                  item
                  xs={12}
                  key={field.id}
                  sx={{
                    width: "100%",
                    opacity: fieldDisabled ? 0.45 : 1,
                    pointerEvents: fieldDisabled ? "none" : "auto",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                      mt: 1,
                    }}
                  >
                    {field.options.length === 0 ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          No sources found
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          color="companyBlue"
                          target="_blank"
                          href="https://icreate-campaign.com/admin/Campaigns/CreateCamp?campType="
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            color: "text.primary",
                          }}
                        >
                          Create Campaign
                        </Button>
                      </Stack>
                    ) : (
                      <Box sx={{ width: "854px", maxWidth: "100%" }}>
                        <FieldLabelWithHelp
                          label={field.label}
                          tooltip={field.tooltip}
                          sx={{ mb: 1 }}
                        />
                        <MultiSelectWithSelectAll
                          label=""
                          options={field.options}
                          value={formData[field.id] || []}
                          onChange={(newValues) => {
                            setFormData((prev) => ({
                              ...prev,
                              [field.id]: newValues,
                            }));
                          }}
                        />
                        {field.helperText ? (
                          <FormHelperText sx={{ color: "rgba(255,255,255,0.65)", ml: 0 }}>
                            {field.helperText}
                          </FormHelperText>
                        ) : null}
                      </Box>
                    )}
                  </Box>
                </Grid>
              );

            case "timeRange":
              const timeRangeError = getTimeRangeError(field.id);
              const hasTimeRangeError = Boolean(
                timeRangeError && touched[field.id]
              );

              return (
                <Grid item xs={12} key={field.id}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: 1,
                      width: "100%",
                      opacity: fieldDisabled ? 0.45 : 1,
                      pointerEvents: fieldDisabled ? "none" : "auto",
                    }}
                  >
                    <FieldLabelWithHelp label={field.label} tooltip={field.tooltip} />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <SingleInputTimeRangeField
                        value={[
                          formData[field.id]?.[0]
                            ? dayjs(formData[field.id][0], "HH:mm")
                            : null,
                          formData[field.id]?.[1]
                            ? dayjs(formData[field.id][1], "HH:mm")
                            : null,
                        ]}
                        onChange={(newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            [field.id]: newValue
                              ? [
                                  newValue[0]?.format("HH:mm") || null,
                                  newValue[1]?.format("HH:mm") || null,
                                ]
                              : [null, null],
                          }));
                          handleTouched(field.id);
                        }}
                        onBlur={() => handleTouched(field.id)}
                        format="HH:mm"
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 50,
                          },
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: hasTimeRangeError,
                            helperText:
                              touched[field.id] && timeRangeError
                                ? timeRangeError
                                : "",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>
              );
            case "timePicker":
              const timeValue = formData[field.id]
                ? dayjs(formData[field.id], "HH:mm")
                : null;
              return (
                <Grid item xs={12} key={field.id}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: 1,
                      width: "100%",
                      opacity: fieldDisabled ? 0.45 : 1,
                      pointerEvents: fieldDisabled ? "none" : "auto",
                    }}
                  >
                    <FieldLabelWithHelp label={field.label} tooltip={field.tooltip} />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        value={timeValue && timeValue.isValid() ? timeValue : null}
                        onChange={(newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            [field.id]:
                              newValue && dayjs(newValue).isValid()
                                ? dayjs(newValue).format("HH:mm")
                                : "",
                          }));
                        }}
                        onClose={() => handleTouched(field.id)}
                        ampm={false}
                        format="HH:mm"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            onBlur: () => handleTouched(field.id),
                            error:
                              field.required &&
                              touched[field.id] &&
                              !formData[field.id],
                            helperText:
                              field.required && touched[field.id] && !formData[field.id]
                                ? "Field is required"
                                : "",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>
              );
            case "conditionBuilder":
              return (
                <Grid item key={field.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" sx={{ color: "#0DA3E2", fontWeight: 600 }}>
                      תנאים
                    </Typography>
                    <FieldHelpTooltip title={sectionTooltips.conditionBuilder} />
                  </Box>
                  <ConditionBuilder
                    value={formData[field.id]}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        //because it ONLY references field id, and we add the fields later, it fails?
                        [field.id]: value,
                      })
                    }
                    conditionTypes={field.conditionTypes}
                    globalMode={conditionGlobalMode}
                    onGlobalModeChange={setConditionGlobalMode}
                    hideGlobalToggle
                  />
                </Grid>
              );
            case "pipeBuilder":
              return (
                <Grid item key={field.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                    <Typography variant="body2" sx={{ color: "#0DA3E2", fontWeight: 600 }}>
                      בונה שאילתה
                    </Typography>
                    <FieldHelpTooltip title={sectionTooltips.pipeBuilder} />
                  </Box>
                  <PipeBuilderMainMenu
                    value={formData[field.id]}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        //because it ONLY references field id, and we add the fields later, it fails?
                        [field.id]: value,
                      })
                    }
                    queryTypes={queryTypesFlat}
                    leadAgeRestriction={formData.leadAgeRestriction}
                  />
                </Grid>
              );
            case "leadAgeRestriction":
              return (
                <Grid item xs={12} key={field.id}>
                  <LeadAgeRestrictionPanel
                    value={formData[field.id]}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        [field.id]: normalizeLeadAgeRestriction(value),
                      })
                    }
                  />
                </Grid>
              );
            case "dateTimePicker":
              const dtValue = formData[field.id]
                ? dayjs(formData[field.id])
                : null;
              const startRangeValue = formData.startDateTime
                ? dayjs(formData.startDateTime)
                : null;
              const endRangeValue = formData.endDateTime
                ? dayjs(formData.endDateTime)
                : null;
              const hasRangeOrderError =
                field.id === "endDateTime" &&
                startRangeValue?.isValid() &&
                endRangeValue?.isValid() &&
                !endRangeValue.isAfter(startRangeValue);
              const dtError =
                touched[field.id] &&
                (hasRangeOrderError ||
                  (field.required &&
                    (!dtValue || !dtValue.isValid() || !dtValue.isAfter(dayjs()))));

              return (
                <Grid item xs={12} key={field.id}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: 1,
                      width: "100%",
                    }}
                  >
                    <FieldLabelWithHelp label={field.label} tooltip={field.tooltip} />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        value={dtValue}
                        onChange={(newVal) => {
                          setFormData((prev) => ({
                            ...prev,
                            [field.id]: newVal
                              ? newVal.format("YYYY-MM-DDTHH:mm:ssZ")
                              : null,
                          }));
                          handleTouched(field.id);
                        }}
                        disablePast
                        ampm={false}
                        format="DD/MM/YYYY HH:mm"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            onBlur: () => handleTouched(field.id),
                            error: dtError,
                            helperText: dtError
                              ? hasRangeOrderError
                                ? "End date & time must be after start date & time"
                                : "Please pick a future date & time"
                              : "",
                            sx: {
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                height: 50,
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>
              );

            default:
              return null;
          }
          })}
      </Grid>
      </FormTemplate>

      {hasCustomQuerySaveConfig && (
        <Dialog
          open={saveConfigOpen}
          onClose={() => setSaveConfigOpen(false)}
          PaperProps={{
            sx: {
              width: "640px",
              maxWidth: "95vw",
              bgcolor: COLOR_SURFACE,
              color: "#fff",
              border: "1px solid rgba(173, 180, 189, 0.45)",
              borderRadius: "3px",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: COLOR_HEADER,
              color: COLOR_ACCENT,
              fontWeight: 700,
              borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            Save Query
            <FieldHelpTooltip title={sectionTooltips.saveQuery} iconSize={16} />
          </DialogTitle>
          <DialogContent sx={{ px: 3, pt: "18px !important", pb: 2 }}>
            <Typography sx={{ color: "rgba(255,255,255,0.9)", mb: 1.5 }}>
              Save query for reuse across this brand
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <TextField
                label="Query Name"
                value={saveConfig.name}
                onChange={(e) => {
                  setSaveConfig((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }));
                  if (saveConfigError) setSaveConfigError("");
                  if (saveConfigSuccess) setSaveConfigSuccess("");
                }}
                fullWidth
                size="small"
                error={Boolean(saveConfigError)}
                helperText={saveConfigError}
                InputLabelProps={{
                  sx: { color: "rgba(255,255,255,0.75)" },
                }}
                sx={{
                  borderRadius: "7px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "7px",
                    color: "#fff",
                    bgcolor: COLOR_HEADER,
                    "& fieldset": { borderColor: "rgba(173, 180, 189, 0.45)" },
                    "&:hover fieldset": {
                      borderColor: "rgba(173, 180, 189, 0.65)",
                    },
                    "&.Mui-focused fieldset": { borderColor: "#0DA3E2" },
                  },
                }}
                InputProps={{
                  sx: {
                    height: 56,
                    fontSize: "0.95rem",
                    px: "14px",
                  },
                }}
              />

              <TextField
                label="Query Description"
                value={saveConfig.description}
                onChange={(e) => {
                  setSaveConfig((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }));
                  if (saveConfigSuccess) setSaveConfigSuccess("");
                }}
                fullWidth
                size="small"
                InputLabelProps={{
                  sx: { color: "rgba(255,255,255,0.75)" },
                }}
                sx={{
                  borderRadius: "7px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "7px",
                    color: "#fff",
                    bgcolor: COLOR_HEADER,
                    "& fieldset": { borderColor: "rgba(173, 180, 189, 0.45)" },
                    "&:hover fieldset": {
                      borderColor: "rgba(173, 180, 189, 0.65)",
                    },
                    "&.Mui-focused fieldset": { borderColor: "#0DA3E2" },
                  },
                }}
                InputProps={{
                  sx: {
                    height: 56,
                    fontSize: "0.95rem",
                    px: "14px",
                  },
                }}
              />
            </Box>
            {saveConfigSuccess && (
              <Typography sx={{ mt: 1, color: "#0DA3E2", fontWeight: 600 }}>
                {saveConfigSuccess}
              </Typography>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 2.5,
              pt: 1,
              gap: 1,
              justifyContent: "flex-end",
              bgcolor: COLOR_SURFACE,
            }}
          >
            <Button
              onClick={() => {
                setSaveConfigOpen(false);
                setSaveConfigError("");
                setSaveConfigSuccess("");
              }}
              variant="outlined"
              sx={{
                width: "187px",
                height: "50px",
                minWidth: "187px",
                borderRadius: "7px",
                borderColor: COLOR_ACCENT,
                color: "#FFFFFF",
                textTransform: "none",
                "&:hover": {
                  borderColor: COLOR_ACCENT,
                  bgcolor: "rgba(13, 163, 226, 0.08)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const trimmedName = (saveConfig.name || "").trim();
                if (!trimmedName) {
                  setSaveConfigError("Query Name is required.");
                  return;
                }
                try {
                  let savedTemplate = null;
                  if (onSaveTemplate) {
                    savedTemplate = await onSaveTemplate({
                      savedQueryId: saveConfig.savedId,
                      name: trimmedName,
                      description: (saveConfig.description || "").trim(),
                      formData,
                    });
                  }

                  setSaveConfig((prev) => ({
                    ...prev,
                    enabled: true,
                    name: trimmedName,
                    description: (prev.description || "").trim(),
                    savedId: savedTemplate?.Id || prev.savedId || null,
                  }));
                  setSaveConfigError("");
                  setSaveConfigSuccess("Saved successfully.");
                } catch (error) {
                  setSaveConfigError(
                    error?.message || "Failed to save query template."
                  );
                }
              }}
              variant="outlined"
              sx={{
                width: "187px",
                height: "50px",
                minWidth: "187px",
                borderRadius: "7px",
                borderColor: COLOR_ACCENT,
                color: "#FFFFFF",
                textTransform: "none",
                "&:hover": {
                  borderColor: COLOR_ACCENT,
                  bgcolor: "rgba(13, 163, 226, 0.08)",
                },
              }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default NodesDynamicForm;
